/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo } from "react";
import NiceModal from "@ebay/nice-modal-react";
import { ProjectOrFolderDownloadModal } from "components/_Modals/ProjectDownloadModal/ProjectOrFolderDownloadModal";
import { containsConfig, getScrollToIndex } from "components/ProjectTree/utils";
import {
  useCopiedItemStore,
  useProjectDataStore,
  useProjectUIStore,
} from "stores/projectStore";
import * as identify from "utils/treeUtils";
import { useDeleteConfirmationCheck } from "components/ProjectTree/hooks";
import { NodeKinds } from "interfaces/enums";
import { toast } from "react-toastify";
import { useFetchProject } from "hooks/projectHooks";
import { useAppBaseContext } from "providers";
import { DuplicateWizardInProjectMut } from "api/mutations";
import { fetchData } from "hooks";
import { WizardLink } from "../WizardLink";
import { NodeTitle } from "../NodeTitle";
import { PasteButton } from "../Buttons/PasteButton";
import { LeftControls, RightControls } from "./FolderControls";
import { EditNodeTypes } from "../EditNodeTypes";

export const Folder = ({
  node,
  options,
  nodeActions,
  translations,
  setRedrawIndex,
  setScrollToIndex,
  willReceiveDrop,
  handleToggleEditMode,
  setIsEditMode,
  isEditMode,
  handleToggleNodeActiveStatus,
}) => {
  const deleteConfirmationCheck = useDeleteConfirmationCheck();
  const copiedItem = useCopiedItemStore((state) => state.copiedItem);
  const isHoveringLeftControls = useCopiedItemStore(
    (state) => state.isHoveringLeftControls,
  );
  const setIsHoveringLeftControls = useCopiedItemStore(
    (state) => state.setIsHoveringLeftControls,
  );
  const toggleNode = useProjectUIStore((state) => state.toggleNode);
  const projectId = useProjectDataStore((state) => state.projectId);
  const { lang } = useAppBaseContext();

  const refetchProject = useFetchProject();
  // Dependancies
  const isRootNode = node.data.kind === NodeKinds.ROOT;
  const { folderName, id } = node.data;
  const isWizard = node.data.kind.startsWith("WIZARD");
  const isWizardRoot = node.data.kind === NodeKinds.WIZARD_ROOT_FOLDER;
  const nodeId = node.id;
  const nodeChildren = node.children;
  const isPasteMode = !!copiedItem;
  const isNodeOpen = node.isOpen;
  const hasChildren = nodeChildren && nodeChildren.length > 0;
  const isNodeOptimistic = nodeId.startsWith("temp__");
  const { rowIndex } = node;

  const useRedraw =
    (fn = () => {}) =>
    () => {
      setRedrawIndex(rowIndex);
      fn();
    };

  useEffect(() => {
    if (willReceiveDrop) {
      setRedrawIndex(rowIndex);
    }
  }, [willReceiveDrop]);

  const handleCopyNode = () => nodeActions.copyNode(node);

  const handleDeleteNode = useRedraw(() => {
    deleteConfirmationCheck(node, () => nodeActions.deleteNode(node));
  });
  const handleCreateNode = useRedraw(() => {
    const index = getScrollToIndex(node);
    nodeActions.createNode(node);
    setScrollToIndex(index);
  });
  const onToggleNode = useCallback(() => {
    node.toggle();
    toggleNode(node.data.id);
  }, [node, toggleNode]);
  const handleUpdateNode = (newTitle) =>
    nodeActions.updateNode(node, { folderName: newTitle });
  const handleDuplicateNode = useRedraw(() => {
    if (isWizard) {
      fetchData({
        query: DuplicateWizardInProjectMut,
        retry: 0,
        variables: {
          input: { projectId, nodeId: node.id, languageCode: lang },
        },
        onError: (error) => {
          toast.error(error.message);
        },
        onSuccess: ({ data }) => {
          if (data.replicateWizard?.status === "SUCCESS") {
            refetchProject();
          }
          return false;
        },
      });
      return;
    }
    const index = getScrollToIndex(node.parent);
    nodeActions.duplicateNode(node);
    setScrollToIndex(index);
  });

  const openDownloadModal = useCallback(() => {
    if (isWizardRoot) {
      NiceModal.show(ProjectOrFolderDownloadModal, {
        modalSubtitle: folderName,
        wizardId: nodeId,
        projectId,
      });
      return;
    }
    if (!isWizard) {
      nodeActions.openNodeDownloadModal(node);
    }
  }, [id, folderName]);

  const hasConfig = useMemo(() => containsConfig(node), [node]);

  return (
    <>
      <div className="d-flex flex-row align-items-center h-100 overflow-hidden">
        <div
          className="d-flex align-items-center h-100"
          onMouseEnter={() => setIsHoveringLeftControls(true)}
          onMouseLeave={() => setIsHoveringLeftControls(false)}
        >
          <LeftControls
            data={node.data}
            handleToggleNode={onToggleNode}
            handleCreateNode={handleCreateNode}
            isNodeOpen={isNodeOpen}
            hasChildren={hasChildren}
            options={options}
            translations={translations}
            nodeId={node.data.id}
          />
        </div>
        <NodeTitle
          title={folderName}
          isEditMode={isEditMode === EditNodeTypes.TITLE}
          setIsEditMode={setIsEditMode}
          handleToggleEditMode={handleToggleEditMode}
          isEditable={!isWizard && !isRootNode && !options.editNodeDisabled}
          handleUpdateTitle={handleUpdateNode}
          className="ms-2 my-1"
          nodeId={nodeId}
        />
        {isWizardRoot && (
          <WizardLink
            node={node}
            params={{ projectId, nodeId: node.data.id }}
            text={translations.folder.wizardLink}
            disabled={isPasteMode}
            handleToggleNodeActiveStatus={handleToggleNodeActiveStatus}
          />
        )}
        {isPasteMode &&
          !isWizard &&
          !identify.isTroxNetcom(node.data) &&
          !isHoveringLeftControls && (
            <PasteButton nodeId={nodeId} text={translations.folder.pasteNode} />
          )}
      </div>
      {!isRootNode && (
        <RightControls
          data={node.data}
          handleToggleEditMode={handleToggleEditMode}
          handleCopyNode={handleCopyNode}
          handleDeleteNode={handleDeleteNode}
          handleDuplicateNode={handleDuplicateNode}
          isPasteMode={isPasteMode}
          hasChildren={hasChildren}
          options={options}
          isNodeOptimistic={isNodeOptimistic}
          translations={translations}
          nodeId={node.data.id}
          openDownloadModal={openDownloadModal}
          hasConfig={hasConfig}
        />
      )}
    </>
  );
};
